import dayjs from 'dayjs';
import { LANGUAGE_LIST, SEX_LIST } from '@/common/constants/common';
import { DISPLAY_DATE_FORMAT } from '@/utils/dateHelper';
import { showLabel } from '@/utils/helper';
import { isEstimatedAgeSource } from '@/utils/ageHelper';

/** Values of one side, keyed like the member / family member detail DTOs. */
export type LinkRecordValues = Record<string, any>;

interface LinkSyncField {
	key: string;
	label: string;
	/** Display text; the raw `key` value is compared. */
	display?: (record: LinkRecordValues) => any;
}

const desc = (key: string) => (record: LinkRecordValues) =>
	record[`${key}Desc`];

/** DMS-REQ-WEB-CHA-005 §5.3: the fields copied on linking and kept in sync. */
export const LINK_SYNC_FIELDS: LinkSyncField[] = [
	{ key: 'nameEn', label: 'member.name_en' },
	{ key: 'nameZh', label: 'member.name_zh' },
	{
		key: 'sex',
		label: 'member.sex',
		display: (r) => (r.sex == null ? null : showLabel(r.sex, SEX_LIST)),
	},
	{ key: 'hkid', label: 'member.HKID' },
	{
		key: 'dateOfBirth',
		label: 'member.dateOfBirth',
		display: (r) =>
			r.dateOfBirth
				? dayjs(r.dateOfBirth).locale('en').format(DISPLAY_DATE_FORMAT)
				: null,
	},
	// CHA-011 §5.4: this year's estimate, synced with the age source.
	{ key: 'estimatedAge', label: 'memberAge.estimatedAge' },
	{ key: 'education', label: 'member.education', display: desc('education') },
	{ key: 'phone', label: 'member.mobilePhone' },
	{ key: 'homeTel', label: 'member.homeTel' },
	{ key: 'email', label: 'member.email' },
	{
		key: 'addressLanguage',
		label: 'member.addressLanguage',
		display: (r) =>
			r.addressLanguage == null
				? null
				: showLabel(r.addressLanguage, LANGUAGE_LIST),
	},
	{ key: 'region', label: 'member.region', display: desc('region') },
	{ key: 'district', label: 'member.district', display: desc('district') },
	{
		key: 'subdistrict',
		label: 'member.subdistrict',
		display: desc('subdistrict'),
	},
	{
		key: 'estateCourtVillage',
		label: 'member.estateCourtVillage',
		display: desc('estateCourtVillage'),
	},
	{ key: 'roadStreet', label: 'member.roadStreet' },
	{ key: 'roadStreetNo', label: 'member.roadStreetNo' },
	{
		key: 'buildingBlock',
		label: 'member.buildingBlock',
		display: desc('buildingBlock'),
	},
	{ key: 'buildingBlockSpecify', label: 'member.buildingBlockSpecify' },
	{ key: 'floor', label: 'member.floor' },
	{ key: 'flat', label: 'member.flat' },
];

const normalize = (value: any) =>
	value === null || value === undefined ? '' : String(value).trim();

export interface LinkFieldRow {
	id: string;
	label: string;
	current: any;
	next: any;
	changed: boolean;
	cleared: boolean;
}

/** Only the value of the chosen age source counts. */
const withAgeSource = (record: LinkRecordValues): LinkRecordValues =>
	isEstimatedAgeSource(record.ageSource)
		? { ...record, dateOfBirth: null }
		: { ...record, estimatedAge: null };

/** One row per synced field: `target` is overwritten by `source` on linking. */
export const buildLinkFieldRows = (
	sourceRecord: LinkRecordValues,
	targetRecord: LinkRecordValues
): LinkFieldRow[] => {
	const source = withAgeSource(sourceRecord);
	const target = withAgeSource(targetRecord);
	return LINK_SYNC_FIELDS.map((field) => {
		const show = (record: LinkRecordValues) =>
			field.display ? field.display(record) : record[field.key];
		const next = normalize(source[field.key]);
		const current = normalize(target[field.key]);
		return {
			id: field.key,
			label: field.label,
			current: show(target),
			next: show(source),
			// Dates may come back with a time part on one side.
			changed:
				field.key === 'dateOfBirth' && next && current
					? !dayjs(next).isSame(current, 'day')
					: next !== current,
			cleared: !next && !!current,
		};
	});
};
